import { User } from '../models/user'
import { IdentityConfig } from '../configs/identity.config'
import { CommandResponse, LocaleService } from 'shared-services'
import { SignOutErrorCode } from '../error-codes/sign-out.error-code'
import { SignInErrorCode } from '../error-codes/sign-in.error-code'
import { SignUpErrorCode } from '../error-codes/sign-up.error-code'
import { ConfirmEmailErrorCode } from '../error-codes/confirm-email.error-code'
import { ResetPasswordErrorCode } from '../error-codes/reset-password.error-code'
import { SetUsernameErrorCode } from '../error-codes/set-username.error-code'
import { SetNameErrorCode } from '../error-codes/set-name.error-code'
import { RequestPasswordResetErrorCode } from '../error-codes/request-password-reset.error-code'
import { UserGetterService } from './user.getter-service'

export class UserService {
  public constructor(
    private readonly identityConfig: IdentityConfig,
    private readonly localeService: LocaleService,
    private readonly userGetterService: UserGetterService,
  ) {}

  public async getUser(): Promise<User> {
    return await this.userGetterService.getUser()
  }

  public async signOut(): Promise<CommandResponse<SignOutErrorCode>> {
    const body = {}

    const response = await fetch(
      `${this.identityConfig.apiBaseUrl}/v1/sign-out`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return (await response.json()) as CommandResponse<SignOutErrorCode>
  }

  public async signIn(
    emailOrUsername: string,
    password: string,
  ): Promise<CommandResponse<SignInErrorCode>> {
    const body = { emailOrUsername, password }

    const response = await fetch(
      `${this.identityConfig.apiBaseUrl}/v1/sign-in`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return (await response.json()) as CommandResponse<SignInErrorCode>
  }

  public async signUp(
    email: string,
    password: string,
  ): Promise<CommandResponse<SignUpErrorCode>> {
    const localeId = this.localeService.languageLocale.localeId
    const body = { email, password, localeId }

    const response = await fetch(
      `${this.identityConfig.apiBaseUrl}/v1/sign-up`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return (await response.json()) as CommandResponse<SignUpErrorCode>
  }

  public async confirmEmail(
    email: string,
    token: string,
  ): Promise<CommandResponse<ConfirmEmailErrorCode>> {
    const body = { email, token }

    const response = await fetch(
      `${this.identityConfig.apiBaseUrl}/v1/confirm-email`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return (await response.json()) as CommandResponse<ConfirmEmailErrorCode>
  }

  public async requestPasswordReset(
    email: string,
  ): Promise<CommandResponse<RequestPasswordResetErrorCode>> {
    const localeId = this.localeService.languageLocale.localeId
    const body = { email, localeId }

    const response = await fetch(
      `${this.identityConfig.apiBaseUrl}/v1/request-password-reset`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return (await response.json()) as CommandResponse<RequestPasswordResetErrorCode>
  }

  public async resetPassword(
    email: string,
    token: string,
    newPassword: string,
  ): Promise<CommandResponse<ResetPasswordErrorCode>> {
    const body = { email, resetCode: token, newPassword }

    const response = await fetch(
      `${this.identityConfig.apiBaseUrl}/v1/reset-password`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return (await response.json()) as CommandResponse<ResetPasswordErrorCode>
  }

  public async setUsername(
    username: string,
  ): Promise<CommandResponse<SetUsernameErrorCode>> {
    const body = { username }

    const response = await fetch(
      `${this.identityConfig.apiBaseUrl}/v1/set-username`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return (await response.json()) as CommandResponse<SetUsernameErrorCode>
  }

  public async setFirstName(
    firstName: string,
  ): Promise<CommandResponse<SetNameErrorCode>> {
    return await this.setName(firstName, '/vi/set-first-name')
  }

  public async setLastName(
    lastName: string,
  ): Promise<CommandResponse<SetNameErrorCode>> {
    return await this.setName(lastName, '/vi/set-last-name')
  }

  private async setName(
    name: string,
    endpoint: string,
  ): Promise<CommandResponse<SetNameErrorCode>> {
    const body = { name }

    const response = await fetch(
      `${this.identityConfig.apiBaseUrl}${endpoint}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return (await response.json()) as CommandResponse<SetNameErrorCode>
  }
}
